import styled, { useTheme } from 'styled-components'
import { useSpring, animated, useTransition } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import { IoAdd } from 'react-icons/io5'
import { useEffect, useState } from 'react'
import { Strong, ChartTitle, ChartDescription, Source, Notes } from '../styles'
import { device } from '../breakpoints'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'

const Simulator = () => {
  const theme = useTheme()
  const groups =
    theme.tenant === '24heures'
      ? new Map([
          [
            1,
            {
              seats: 9,
              requiredVotes: 45754,
              requiredPeople: 2408,
              looser: 4,
            },
          ],
          [
            2,
            {
              seats: 3,
              requiredVotes: 32626,
              requiredPeople: 1717,
              looser: 4,
            },
          ],
          [
            3,
            {
              seats: 0,
              requiredVotes: 5149,
              requiredPeople: 271,
              looser: 4,
            },
          ],
          [
            4,
            {
              seats: 2,
              requiredVotes: 163994,
              requiredPeople: 8631,
              looser: 5,
            },
          ],
          [
            5,
            {
              seats: 5,
              requiredVotes: 141503,
              requiredPeople: 7448,
              looser: 4,
            },
          ]
        ])
      : new Map([
          [
            1,
            {
              seats: 3,
              requiredVotes: 41982,
              requiredPeople: 3498,
              looser: 5,
            },
          ],
          [
            2,
            {
              seats: 2,
              requiredVotes: 32142,
              requiredPeople: 2678,
              looser: 5,
            },
          ],
          [
            3,
            {
              seats: 0,
              requiredVotes: 73601,
              requiredPeople: 6133,
              looser: 5,
            },
          ],
          [
            4,
            {
              seats: 6,
              requiredVotes: 50224,
              requiredPeople: 4185,
              looser: 5,
            },
          ],
          [
            5,
            {
              seats: 1,
              requiredVotes: 97399,
              requiredPeople: 8117,
              looser: 4,
            },
          ],
        ])

  // Bind it to a component
  return (
    <Container>
      <TextWidth>
        <ChartTitle>Résultat des dernières élections fédérales</ChartTitle>
        <ChartDescription>
          Ajoutez un siège à une alliance pour trouver combien de voix il lui
          aurait fallu pour le gagner et au détriment de quel autre
          apparentement lors des dernières élections fédérales de 2019 dans le
          canton de {theme.tenant === '24heures' ? 'Vaud' : 'Genève'}.
        </ChartDescription>
        {/* <DropdownContainer>
          <Dropdown
            handleChange={(value) => {
              console.log(value)
            }}
            placeholder='Vaud'
            items={[
              { label: 'Vaud', value: 'vd' },
              { label: 'Genève', value: 'ge' },
            ]}
          />
        </DropdownContainer> */}
      </TextWidth>
      <Content>
        <DraggableList items={groups} />
      </Content>
      <TextWidth>
        <Notes>
          En ne tenant compte que des apparentements principaux et des votes par
          liste.
        </Notes>
        <Source>Source: Tamedia, Chancellerie fédérale</Source>
      </TextWidth>
    </Container>
  )
}

const DraggableList = ({
  items,
}: {
  items: Map<
    number,
    {
      seats: number
      requiredVotes: number
      requiredPeople: number
      looser: number
    }
  >
}) => {
  const theme = useTheme()
  const [modifiedItems, setModifiedItems] = useState(items)
  const [selectedItem, setSelectedItem] = useState<{
    name: number
    data: any
  } | null>(null)

  const appNamesData:any = {
    '24heures': ['Gauche/Verts', 'UDC', 'Centre', "Verts'lib/Pirate", 'PLR'],
    'tdg': ['PLR/PDC', 'UDC/MCG', 'PBD/PCD', 'Gauche/Verts', "Vert'libéraux"],
  }
  const appName = appNamesData[theme.tenant] || appNamesData['tdg']

  useEffect(() => {
    setModifiedItems(items)
  }, [items])

  const handleClick = (name: number) => {
    const newItems = new Map(items)
    const item = newItems.get(name)
    if (item === undefined) return
    newItems.set(name, { ...item, seats: item.seats + 1 })
    // newItems.set(item.looser, { ...item, seats: item.seats - 1 })
    setModifiedItems(new Map(newItems))
    setSelectedItem({ name: name, data: item })
  }

  return (
    <ListContainer>
      {[...modifiedItems].map(([name, data], index) => {
        return (
          <DgLineContainer>
            <DglLabel>
              <DglName>{name} - {appName[name - 1]}</DglName>
            </DglLabel>

            <DglLine key={index}>
              <DglSeatsContainer>
                <DglSeats>
                  <Seats
                    winner={selectedItem && name === selectedItem.name}
                    looser={selectedItem && name === selectedItem.data.looser}
                    count={data.seats}
                  />
                </DglSeats>
              </DglSeatsContainer>

              {selectedItem && name === selectedItem.name && (
                <Message>
                  Il faudrait{' '}
                  <Strong>
                    {selectedItem.data.requiredPeople.toLocaleString('fr-CH')}
                  </Strong>{' '}
                  électeurs de plus en faveur de cette alliance pour gagner un
                  siège (
                  {selectedItem.data.requiredVotes.toLocaleString('fr-CH')}{' '}
                  suffrages). L'apparentement <b>{appName[selectedItem.data.looser - 1]}</b> en perdrait alors
                  un.
                </Message>
              )}
            </DglLine>

            <DglSeatsValue>
              <Value>
                {selectedItem && name === selectedItem.name ? (
                  <>
                    {data.seats - 1}{' '}
                    <span style={{ fontWeight: 200 }}>+ 1</span>
                  </>
                ) : (
                  data.seats
                )}{' '}
                {selectedItem && name === selectedItem.data.looser && (
                  <span style={{ fontWeight: 200 }}>- 1</span>
                )}{' '}
                siège{data.seats > 1 ? 's' : ''}
              </Value>
              <DglAddSeat
                onClick={() => {
                  handleClick(name)
                }}
              >
                <IoAdd />
              </DglAddSeat>
            </DglSeatsValue>
          </DgLineContainer>
        )
      })}
    </ListContainer>
  )
}

const Message = styled.div`
  font-size: .85em;
  padding: 10px;
  margin-top: 10px;
`

const ListContainer = styled.div`
  // padding: 15px;
  // border: 1px solid rgb(${({ theme }) => theme.grayColorMedium});
  // box-shadow: 0px 2px 10px rgba(5, 0, 56, 0.08);
  // border-radius: 16px;
`

const Seats = ({
  count,
  winner,
  looser,
}: {
  count: number
  winner: boolean | null
  looser: boolean | null
}) => {
  // Create an array of items with unique keys
  const items = new Array(count).fill(null).map((_, i) => ({ key: i }))

  const transitions = useTransition(items, {
    // Each item has a unique key
    keys: (item) => item.key,
    // Apply an animation for items that are entering the list
    from: { opacity: 0, scale: 0 },
    enter: { opacity: 1, scale: 1 },
    // Apply an animation for items that are leaving the list
    leave: { opacity: 0, scale: 0 },
  })

  const rad = 50
  const trails = new Array(5)
    .fill(null)
    .map((_, i) => ({ key: i, rd: 1 + Math.random() }))

  return (
    <>
      {transitions((style, item) => (
        <CircleContainer winner={winner} looser={looser}>
          {trails.map((trail, index) => {
            const angle = (index / trails.length) * Math.PI * 2
            return (
              <Trail
                style={{
                  scale: style.opacity.to(
                    (v) => Math.sin(v * Math.PI) * trail.rd * 0.3
                  ),
                  opacity: style.opacity.to((v) => Math.cos(v)),
                  translateX: style.opacity.to(
                    (v) => Math.cos(angle) * v * rad * trail.rd
                  ),
                  translateY: style.opacity.to(
                    (v) => Math.sin(angle) * v * rad * trail.rd
                  ),
                }}
              />
            )
          })}
          <HandleContainer style={style} />
        </CircleContainer>
      ))}
    </>
  )
}

const CircleContainer = styled.div<{
  winner: boolean | null
  looser: boolean | null
}>`
  position: relative;
  padding: 2px;

  &:last-of-type {
    div {
      background: repeating-linear-gradient(
        50deg,
        ${({ theme, winner, looser }) =>
          winner || looser ? 'transparent' : theme.colorMain},
        ${({ theme, winner, looser }) =>
            winner || looser ? 'transparent' : theme.colorMain}
          2px,
        rgba(
            ${({ theme, winner, looser }) =>
              winner ? theme.colorMain : theme.colorBlueDark}
          )
          2px,
        rgba(
            ${({ theme, winner, looser }) =>
              winner ? theme.colorMain : theme.colorBlueDark}
          )
          3px
      );
    }
  }
`

const HandleContainer = styled(animated.div)`
  width: 30px;
  aspect-ratio: 1;
  background-color: rgba(${({ theme }) => theme.colorMain});
  // border: 2px solid rgba(${({ theme }) => theme.colorMainDark});
  cursor: grab;
  user-select: none;
  position: relative;
  z-index: 1;

  &:last-child {
    margin-right: 0;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 20px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 30px;
  }
`

const Trail = styled(animated.div)`
  width: 10px;
  height: 10px;
  background-color: rgba(${({ theme }) => theme.colorMain});
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -5px;
  margin-left: -5px;
  border-radius: 50%;
  z-index: 0;
`

const Handle = () => {
  const [{ x, y, shadow, scale }, api] = useSpring(() => ({
    x: 0,
    y: 0,
    shadow: 1,
    scale: 1,
  }))

  // Set the drag hook and define component movement based on gesture data
  const bind = useDrag(({ down, movement: [mx, my] }) => {
    api.start({
      x: down ? mx : 0,
      y: down ? my : 0,
      shadow: down ? 15 : 1,
      scale: down ? 1.1 : 1,
    })
  })

  return (
    <HandleContainer
      {...bind()}
      style={{
        x,
        y,
        scale,
        boxShadow: shadow.to(
          (s) => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`
        ),
      }}
    />
  )
}

const Container = styled.div`
  margin: 40px 0;
  margin-bottom: 90px;
`

const Content = styled.div`
  flex-direction: column;
  max-width: 1200px;
  width: calc(100% - 40px);
  margin: 20px auto;
  padding: 0 20px;
`

const Value = styled.div`
  font-size: 0.85em;
  color: rgb(${({ theme }) => theme.colorBlueDark});
  font-weight: bold;
  padding: 1px 6px;
  background-color: rgb(${({ theme }) => theme.grayColorLight});
`

const DglSeatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DglSeats = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
`

const DglAddSeat = styled.div`
  width: 50px;
  height: 36px;
  background-color: rgb(${({ theme }) => theme.colorMain});
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2em;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  margin-left: 10px;
  color: rgb(${({ theme }) => theme.colorMode === 'dark' ? theme.siteBackground : '255,255,255'});

  &:hover {
    opacity: 0.8;
  }

  path {
    stroke: rgb(${({ theme }) => theme.colorMode === 'dark' ? theme.siteBackground : '255,255,255'});
  }
`

const DglLabel = styled.div`
  line-height: 1.2;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;

  grid-column: 1 / 1;
  grid-row: 1 / 1;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    min-height: 28px;
    grid-column: 1 / 1;
    grid-row: 1 / 1;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    min-height: 38px;
    grid-column: 1 / 1;
    grid-row: 1 / 1;
  }
`

const DglLine = styled.div`
  line-height: 1.4;
  background-color: rgb(${({ theme }) => theme.grayColorLight});
  border-radius: 0px;
  padding: 0 2px;
  width: 100%;

  grid-column: 2 / 2;
  grid-row: 1 / 1;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-column: 1 / 1;
    grid-row: 2 / 2;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-column: 2 / 2;
    grid-row: 1 / 1;
  }
`

const DglSeatsValue = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  grid-column: 3 / 3;
  grid-row: 1 / 1;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-column: 1 / 1;
    grid-row: 3 / 3;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-column: 3 / 3;
    grid-row: 1 / 1;
  }
`

const DgLineContainer = styled.div`
  margin-bottom: 10px;
  padding-bottom: 10px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  grid-template-rows: 1fr;
  border-bottom: 1px solid rgb(${({ theme }) => theme.grayColorMedium});

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr 2fr 1fr;
    grid-template-rows: 1fr;
  }
`

const DglName = styled.div`
  font-weight: bold;
  font-size: 0.85em;
`

export default Simulator
